import React from 'react';
import * as ToastPrimitives from '@radix-ui/react-toast';
import { cn } from 'lib/utils';
import { Cross2Icon } from '@radix-ui/react-icons';

interface ICustomToastToastProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  variant?: 'warning' | 'success' | 'destructive';
  preventAutoClose?: boolean;
}

const CustomToast: React.FC<ICustomToastToastProps> = ({
  open,
  onOpenChange,
  title,
  description,
  variant = 'warning',
  preventAutoClose = false,
}) => {
  const getVariantClasses = () => {
    switch (variant) {
      case 'warning':
        return 'border-warning bg-warning-background text-warning-high-emphasis';
      case 'success':
        return 'border-success bg-success-background text-success-high-emphasis';
      case 'destructive':
        return 'destructive group border-destructive bg-destructive text-destructive-foreground';
      default:
        return 'border bg-background text-foreground';
    }
  };

  return (
    <ToastPrimitives.Provider swipeDirection="right">
      <ToastPrimitives.Root
        className={cn(
          'group pointer-events-auto relative flex w-full items-center justify-between space-x-2 overflow-hidden rounded-md border p-4 pr-6 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full',
          getVariantClasses()
        )}
        open={open}
        onOpenChange={(newOpen) => {
          if (!preventAutoClose || newOpen) {
            onOpenChange(newOpen);
          }
        }}
      >
        <div className="grid gap-1">
          <ToastPrimitives.Title className="text-sm font-semibold [&+div]:text-xs">
            {title}
          </ToastPrimitives.Title>
          <ToastPrimitives.Description className="text-sm opacity-90">
            {description}
          </ToastPrimitives.Description>
        </div>
        {!preventAutoClose && (
          <ToastPrimitives.Close className="absolute right-1 top-1 rounded-md p-1 text-foreground/50 opacity-0 transition-opacity hover:text-foreground focus:opacity-100 focus:outline-none focus:ring-1 group-hover:opacity-100 group-[.destructive]:text-red-300 group-[.destructive]:hover:text-red-50 group-[.destructive]:focus:ring-red-400 group-[.destructive]:focus:ring-offset-red-600">
            <Cross2Icon className="h-4 w-4" />
          </ToastPrimitives.Close>
        )}
      </ToastPrimitives.Root>
      <ToastPrimitives.Viewport className="fixed top-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px]" />
    </ToastPrimitives.Provider>
  );
};

export default CustomToast;
